import { PropsWithChildren, ReactNode } from "react";
import { Link } from "react-router-dom";
import { styled } from "styled-components";
import { ArrowRight, Check } from "lucide-react";

export const Container = styled.div`
  width: min(calc(100% - 2rem), ${({ theme }) => theme.maxWidth});
  margin-inline: auto;
`;

export const Section = styled.section<{ $compact?: boolean }>`
  padding: ${({ $compact, theme }) => $compact ? theme.spacing.xl : theme.spacing.xxl} 0;
  position: relative;
`;

export const Grid = styled.div<{ $min?: string; $gap?: string }>`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, ${({ $min }) => $min || "250px"}), 1fr));
  gap: ${({ $gap }) => $gap || "1.25rem"};
`;

export const Card = styled.article`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.lg};
  box-shadow: 0 20px 60px ${({ theme }) => theme.colors.shadow};
  backdrop-filter: blur(18px);
  padding: clamp(1.25rem, 3vw, 2rem);
`;

export const Eyebrow = styled.p`
  margin: 0 0 .75rem;
  color: ${({ theme }) => theme.colors.accent};
  text-transform: uppercase;
  letter-spacing: .13em;
  font-size: .78rem;
  font-weight: 850;
`;

export const Heading = styled.h2`
  margin: 0;
  font-size: clamp(2rem, 5vw, 4rem);
  line-height: 1.02;
  letter-spacing: -.045em;
  max-width: 18ch;
`;

export const Lead = styled.p`
  color: ${({ theme }) => theme.colors.muted};
  font-size: clamp(1.05rem, 2vw, 1.25rem);
  max-width: 68ch;
  margin: 1rem 0 0;
`;

export const ButtonLink = styled(Link)<{ $secondary?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: .6rem;
  min-height: 46px;
  padding: .75rem 1.1rem;
  border-radius: ${({ theme }) => theme.radii.pill};
  text-decoration: none;
  font-weight: 800;
  background: ${({ $secondary, theme }) => $secondary ? theme.colors.surfaceElevated : theme.colors.primary};
  color: ${({ $secondary, theme }) => $secondary ? theme.colors.text : "#04131e"};
  border: 1px solid ${({ theme }) => theme.colors.border};
  transition: transform .2s ease, box-shadow .2s ease;
  &:hover { transform: translateY(-2px); box-shadow: 0 12px 35px ${({ theme }) => theme.colors.shadow}; }
`;

export const TextLink = styled(Link)`
  display: inline-flex; align-items: center; gap: .4rem; color: ${({ theme }) => theme.colors.primary};
  font-weight: 800; text-decoration: none;
  &:hover { text-decoration: underline; }
`;

export const ButtonRow = styled.div`
  display: flex; flex-wrap: wrap; gap: .8rem; margin-top: 1.5rem;
`;

export const Pill = styled.span`
  display: inline-flex; align-items: center; min-height: 30px; padding: .25rem .7rem;
  border-radius: ${({ theme }) => theme.radii.pill};
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: .82rem; font-weight: 750;
`;

export const CheckList = styled.ul`
  list-style: none; padding: 0; margin: 1.25rem 0 0; display: grid; gap: .7rem;
  li { display: flex; align-items: flex-start; gap: .65rem; color: ${({ theme }) => theme.colors.muted}; }
  svg { color: ${({ theme }) => theme.colors.accent}; flex: 0 0 auto; margin-top: .18rem; }
`;

export function SectionHeader({ eyebrow, title, lead, action, headingAs = "h2" }: { eyebrow: string; title: string; lead?: string; action?: ReactNode; headingAs?: "h1" | "h2" }) {
  return (
    <div style={{display:"flex", justifyContent:"space-between", gap:"2rem", alignItems:"end", flexWrap:"wrap", marginBottom:"2rem"}}>
      <div>
        <Eyebrow>{eyebrow}</Eyebrow>
        <Heading as={headingAs}>{title}</Heading>
        {lead && <Lead>{lead}</Lead>}
      </div>
      {action}
    </div>
  );
}

export function BulletList({ items }: { items: string[] }) {
  return <CheckList>{items.map(item => <li key={item}><Check size={18} aria-hidden="true" />{item}</li>)}</CheckList>;
}

export function ArrowText({ children }: PropsWithChildren) {
  return <>{children}<ArrowRight size={17} aria-hidden="true" /></>;
}
